<template>
    <div class="search-wrapper">
        <i class="far fa-search"></i>
        <form-input
            ref="search"
            padding-top="0"
            input-padding="0.4rem 2.1rem 0.4rem 2.7rem"
            class="search-input"
            :disabled="disabled"
            v-model="searchParams"
            @input="onInput"
            @keydown="onKeydown"
            @focus="onFocus"
            @blur="onBlur"
        />
        <i class="far fa-times" v-show="searchParams" @click="clear"></i>
        <div class="dropdown" v-show="showDropdown" ref="dropdown" tabindex="0">
            <div
                class="result"
                v-for="(result, index) in results"
                :key="index"
                :class="{ selected: index === selectedIndex }"
                @click="selectResult(index)"
            >
                <div class="spacer">
                    <i v-if="result.icon" :class="result.icon"></i>
                </div>{{ result.text }}
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'SearchBar',
        data() {
            return {
                searchParams: this.initValue,
                results: [],
                hasFocus: false,
                selectedIndex: -1,
                timeout: null,
            };
        },
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            minChars: {
                type: Number,
                default: 1,
            },
            items: {
                type: [Array, Function],
            },
            sort: {
                type: Boolean,
                default: true,
            },
            searchDelay: {
                type: Number,
                default: 0,
            },
            initValue: {
                type: String,
                default: '',
            },
            searchable: {
                type: Array,
            }
        },
        computed: {
            showDropdown() {
                return this.hasFocus && this.results.length > 0;
            },
        },
        methods: {
            onInput(val) {
                this.$emit('input', val);
                this.selectedIndex = -1;
                this.results = [];
                if (this.searchDelay) {
                    if (this.timeout) clearTimeout(this.timeout);
                    this.timeout = setTimeout(this.paramsChanged, this.searchDelay);
                } else {
                    this.paramsChanged();
                }
            },
            async paramsChanged() {
                this.timeout = null;
                let val = this.searchParams;
                if(val.trim().length < this.minChars) val = '';
                this.$emit('change', val);

                if (!val || !this.items) return;

                let results = [];
                if (typeof this.items === 'function') {
                    results = await this.items(val);
                } else if (Array.isArray(this.items)) {
                    const params = val.trim().toLowerCase().split(/\s+/);
                    results = this.items.filter((item) => {
                        let text = '';
                        const searchable = item.searchable || this.searchable;
                        if (searchable) {
                            for (let field of searchable) {
                                text += item[field] + ' ';
                            }
                        } else {
                            text += item.text;
                        }
                        text = text.toLowerCase();
                        for (let param of params) {
                            if (text.indexOf(param) === -1) {
                                return false;
                            }
                        }
                        return true;
                    });
                    if (this.sort) {
                        results.sort((item1, item2) => {
                            if (item1.priority && (!item2.priority || item1.priority > item2.priority)) return -1;
                            if (item2.priority && (!item1.priority || item2.priority > item1.priority)) return 1;
                            return item1.text.toLowerCase() < item2.text.toLowerCase() ? -1 : 1;
                        });
                    }
                }

                this.results = results;
            },
            onKeydown(e) {
                if (e.which === 40) {
                    // Down arrow
                    this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1);
                    e.preventDefault();
                } else if (e.which === 38) {
                    // Up arrow
                    this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
                    e.preventDefault();
                } else if (e.which === 13) {
                    // Enter
                    if (this.selectedIndex >= 0) {
                        this.selectResult(this.selectedIndex);
                    }
                    e.preventDefault();
                }
                this.$emit('keydown', e);
            },
            onFocus(e) {
                this.$emit('focus', e);
                this.hasFocus = true;
            },
            onBlur(e) {
                if (this.$refs.dropdown === e.relatedTarget) return;
                this.$emit('blur', e);
                this.hasFocus = false;
            },
            async focus() {
                await this.$nextTick();
                this.$refs.search.focus();
            },
            selectResult(index) {
                this.$emit('result-selected', this.results[index]);
                this.results = [];
                this.searchParams = '';
                this.$refs.search.blur();
            },
            clear() {
                this.searchParams = '';
                this.onInput('');
                this.focus();
            }
        },
    };
</script>

<style lang="scss" scoped>
    .search-wrapper {
        position: relative;
        z-index: 2;

        > i {
            color: $color-label;
            position: absolute;
            top: 10px;
            z-index: 3;

            &.fa-search {
                left: 1rem;
                pointer-events: none;
            }

            &.fa-times {
                right: 1rem;
                cursor: pointer;
                padding: 0 2px;
                top: 50%;
                font-size: 1.2em;
                transform: translateY(-50%);

                &:hover {
                    color: $color-focus;
                }
            }
        }

        .search-input {
            flex-grow: 1;

            ::v-deep input {
                height: 32px;
            }
        }
    }

    .dropdown {
        position: absolute;
        top: 2.3rem;
        background-color: $color-white;
        width: 100%;
        z-index: -1;
        @include border;
        @include border-radius-small;
        @include box-shadow-small;
        border-top-color: $color-border;
        max-height: 21rem;
        overflow-y: auto;

        .result {
            padding: 0.4rem 1rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            cursor: pointer;

            &:hover {
                background-color: #f5f5f5;
            }

            &.selected {
                background-color: $color-select;
            }

            .spacer {
                display: inline-block;
                width: 1.8rem;
            }
        }
    }
</style>